interface EmptyStateProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: string;
}

export function EmptyState({ title, description, actionLabel, onAction, icon = "ri-inbox-line" }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-500">
        <i className={icon} aria-hidden="true"></i>
      </div>
      <h3 className="text-base font-semibold text-foreground-900">{title}</h3>
      {description ? <p className="mt-2 max-w-md text-sm text-foreground-500">{description}</p> : null}
      {actionLabel && onAction ? (
        <button
          type="button"
          onClick={onAction}
          className="mt-5 rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  );
}

interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
}

export function ErrorState({
  title = "内容加载失败",
  description = "网络似乎开小差了，请稍后重试。",
  onRetry,
}: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-accent-200 bg-accent-50 px-6 py-16 text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-600">
        <i className="ri-error-warning-line" aria-hidden="true"></i>
      </div>
      <h3 className="text-base font-semibold text-foreground-900">{title}</h3>
      <p className="mt-2 max-w-md text-sm text-foreground-600">{description}</p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mt-5 flex items-center gap-1.5 rounded-md bg-accent-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
        >
          <i className="ri-refresh-line" aria-hidden="true"></i>
          重新加载
        </button>
      ) : null}
    </div>
  );
}

export function Spinner({ label = "加载中…" }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20 text-foreground-500">
      <div className="h-9 w-9 animate-spin rounded-full border-2 border-background-300 border-t-primary-500"></div>
      <span className="text-sm">{label}</span>
    </div>
  );
}

export function SkeletonCardGrid({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-lg border border-background-200 bg-background-50 p-3">
          <div className="skeleton-shimmer mb-3 aspect-[2/3] w-full rounded-md"></div>
          <div className="skeleton-shimmer mb-2 h-4 w-3/4 rounded"></div>
          <div className="skeleton-shimmer h-3 w-1/2 rounded"></div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonList({ count = 4 }: { count?: number }) {
  return (
    <div className="flex flex-col gap-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
          <div className="skeleton-shimmer h-20 w-28 shrink-0 rounded-md"></div>
          <div className="flex-1">
            <div className="skeleton-shimmer mb-3 h-4 w-2/3 rounded"></div>
            <div className="skeleton-shimmer mb-2 h-3 w-full rounded"></div>
            <div className="skeleton-shimmer h-3 w-1/2 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}